import { contentTypes } from 'ggApp/modules/contentful/models/contentTypes';
import {
    mainClient,
    gPageClient,
    mainLongerTimeoutClient,
    gPageLongerTimeoutClient,
} from '../clients';
import { getSupportedLocale } from '../utils/locale';

const isServer = __IS_SERVER__;

function getContentInclusionLevel(contentType) {
    let contentInclusionLevel;
    switch (contentType) {
        case contentTypes.CfGPage:
            contentInclusionLevel = 3;
            break;
        case contentTypes.CfHomePage:
        case contentTypes.CfBusinessLandingPage:
            contentInclusionLevel = 2;
            break;
        default:
            contentInclusionLevel = 1;
    }
    return contentInclusionLevel;
}

function throwCfUnknownError(error, contentType, filters) {
    const throwMappedError = (e) => {
        const errorMessage = e.response?.data?.message || 'Contentful unknown error';
        const errorInstance = new Error(errorMessage);
        errorInstance.data = e.response?.data || {};
        throw errorInstance;
    };

    if (isServer) {
        throwMappedError(error);
    }

    throwMappedError(error);
}

const getClient = (contentType, attempt) => {
    // G-Page content is fetched from a seperate cf space
    if (contentType === contentTypes.CfGPage || contentType === contentTypes.CfBlackFridayContent) {
        if (!isServer && attempt > 0) {
            return gPageLongerTimeoutClient;
        }
        return gPageClient;
    }
    if (!isServer && attempt > 0) {
        return mainLongerTimeoutClient;
    }
    return mainClient;
};

function getCfEntries(contentType, filters = {}) {
    let attempt = 0;
    const getCfEntriesWithRetry = () => {
        const cfClient = getClient(contentType, attempt);
        const { locale } = filters;
        return (
            cfClient
                .getEntries({
                    ...filters,
                    locale: getSupportedLocale(locale),
                    content_type: contentType,
                    include: getContentInclusionLevel(contentType),
                })
                .then((entries) => entries.toPlainObject())
                // eslint-disable-next-line consistent-return
                .catch((error) => {
                    if (error.message.includes('timeout') && attempt < 2) {
                        attempt += 1;
                        return getCfEntriesWithRetry();
                    }
                    throwCfUnknownError(error, contentType, filters);
                })
        );
    };
    return getCfEntriesWithRetry();
}

function getCfEntry(contentType, filters = {}) {
    return getCfEntries(contentType, {
        ...filters,
        limit: 1,
    }).then((entries) => {
        if (entries.items && entries.items.length === 1) {
            return entries.items[0];
        }
        if (contentType === contentTypes.CfGPage) {
            throw new Error(404);
        }
        return null;
    });
}

function getCfEntriesByNames(contentType, names = [], locale = 'de') {
    const list = names.toString();
    return getCfEntries(contentType, {
        'fields.name[in]': list,
        limit: 10,
        locale: getSupportedLocale(locale),
    }).then((entries) => {
        if (entries.items) {
            return entries.items;
        }
        throw new Error(404);
    });
}

function getCfStoreSpecificEntry(contentType, storeCode = 'default', filters = {}) {
    return getCfEntry(contentType, {
        ...filters,
        'fields.storeCode': storeCode,
    });
}

export default {
    getCfEntry,
    getCfStoreSpecificEntry,
    getCfEntriesByNames,
};
